import {
  isPointCloudLoading,
  isPointCloudVisible,
  PointCloudItem,
  useCapabilityRegistryContext,
  useDataContext,
  useFxWorkspaceContext,
} from '@mobile-mapping/providers';
import { FitToViewModelCapabilityCoordinator } from '@trimble/web3d';
import { SelectionSyncCoordinator } from '@trimble/pane-sync';
import { LoadState } from '@trimble/data-loading';
import { PointCloudsTree } from '@trimble/project-data-pointclouds-tree';
import { createMemo, JSX } from 'solid-js';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import { createPointCloudFolderDescendantRefTrnsGetter } from '../fx-workspace-draft-checkbox-sources';
import { fxWorkspaceDraftTrailingContent } from '../fx-workspace-draft-checkboxes';
import { handleNodeSelectionSync } from '../tree-helpers';
import { useLogger } from '@trimble/solidjs-utils';
import { ProjectDataTreeContainerProps } from './shared-panel.types';

export type SharedPointCloudsTreeContainerProps =
  ProjectDataTreeContainerProps<PointCloudItem>;

export const SharedPointCloudsTreeContainer = (
  props: SharedPointCloudsTreeContainerProps
): JSX.Element => {
  const { dataState, dataLoadingService } = useDataContext();
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const fxWorkspace = useFxWorkspaceContext();
  const logger = useLogger().getLogger('SharedPointCloudsTreeContainer');

  const fitToViewCoordinator = capabilityRegistry.get(
    FitToViewModelCapabilityCoordinator
  );
  const selectionSyncCoordinator = capabilityRegistry.get(
    SelectionSyncCoordinator
  );

  const filteredPointClouds = createMemo(() => {
    const pointCloud = dataState.pointCloud;

    if (props.nodes !== undefined) {
      const items = Object.fromEntries(
        props.nodes.flatMap((node) =>
          node.data === undefined ? [] : [[node.id, node.data]]
        )
      ) as Record<string, PointCloudItem>;

      return { ...pointCloud, items };
    }

    if (pointCloud.state !== LoadState.LoadComplete) {
      return pointCloud;
    }

    const items = Object.fromEntries(
      Object.entries(pointCloud.items).filter(
        ([, item]) => item.mission === undefined
      )
    ) as Record<string, PointCloudItem>;

    return { ...pointCloud, items };
  });

  const isNodeSelected = (nodeId: string): boolean =>
    selectionSyncCoordinator()?.selectedModelIds().has(nodeId) ?? false;

  const handleNodeSelect = (
    nodeId: string,
    node: TreeNodeDefinition<PointCloudItem>,
    e: MouseEvent
  ): void => {
    void handleNodeSelectionSync(
      nodeId,
      node,
      e,
      (id) => isPointCloudVisible(dataState, id),
      selectionSyncCoordinator()
    ).catch((error) => logger.error('Error selecting node', node, error));
  };

  const getFolderDescendantRefTrns = createMemo(() => {
    const pointClouds = filteredPointClouds();
    const items =
      pointClouds.state === LoadState.LoadComplete
        ? Object.values(pointClouds.items)
        : [];

    return createPointCloudFolderDescendantRefTrnsGetter(items);
  });

  const cancelPointCloudLoad = (nodeId: string): void => {
    if (isPointCloudLoading(dataState, nodeId)) {
      dataLoadingService.unload('pointCloud', nodeId);
    }
  };

  return (
    <PointCloudsTree
      pointClouds={filteredPointClouds()}
      isNodeLoading={(nodeId) => isPointCloudLoading(dataState, nodeId)}
      isNodeVisible={(nodeId) => isPointCloudVisible(dataState, nodeId)}
      onVisibilityToggle={(nodeId) => {
        if (
          isPointCloudLoading(dataState, nodeId) ||
          isPointCloudVisible(dataState, nodeId)
        ) {
          dataLoadingService.unload('pointCloud', nodeId);
        } else {
          dataLoadingService.load('pointCloud', nodeId);
        }
      }}
      onCancelVisibilityToggle={cancelPointCloudLoad}
      onFitToView={(item) => {
        void fitToViewCoordinator()?.fitToView(item.id);
      }}
      isNodeSelected={isNodeSelected}
      onNodeSelect={handleNodeSelect}
      rowMode={fxWorkspace.isWorkspaceDataEditing() ? 'pick' : 'default'}
      visibleContent={(node) =>
        fxWorkspaceDraftTrailingContent(
          'pointClouds',
          node,
          fxWorkspace.isWorkspaceDataEditing(),
          getFolderDescendantRefTrns()
        )
      }
    />
  );
};
